import styled from '@emotion/styled';
import P2 from '@base/P2/P2';
import Button from '@base/Button/Button';
import Paper from '@base/Paper/Paper';
import { Fonts, Spacings } from '@styles/index';
import { getDateMonthString } from '@utils/dates';
import pushRoute from '@utils/pushRoute';
import { PRACTICE_FLOW_TYPE, PRACTICE_TYPE } from '@cont/Practice/constants';
import { PRACTICE_STARTED } from '@constants/segment';
import usePracticeSegmentAnalytics from '@cont/Practice/usePracticeSegmentAnalytics';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import { useDispatch } from 'react-redux';
import { finishPracticeSession } from '@actions/practiceSession';
import useFinishSummary from '@stores/auto-dpp/dppFinishSummaryStore';
import usePracticeStore from '@stores/planner/practiceStore';
import CardHeader from './CardHeader';

const OuterWrapper = styled(Paper)`
  background-color: var(--color-base-1);
  margin-top: ${Spacings.SPACING_4B};
`;

const StyledButton = styled(Button)`
  border-radius: ${Spacings.SPACING_1B};
  font-size: ${Fonts.SMALL};
  height: 36px;
  color: ${({ $isDarkModeOn, $textColor }) =>
    $isDarkModeOn ? $textColor : ''};
`;

const ActionWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: ${Spacings.SPACING_5B};
  height: ${Spacings.SPACING_14B};
  margin-top: ${Spacings.SPACING_4B};
  border-top: solid 1px var(--color-divider);
`;

const Title = styled(P2)`
  color: var(--color-text-primary);
  font-weight: ${Fonts.SEMIBOLD};
  line-height: 16px;
`;

const SubTitle = styled.div`
  font-size: ${Fonts.EXTRA_SMALL};
  line-height: 14px;
  color: var(--color-text-secondary);
  margin-top: ${Spacings.SPACING_1B};
`;

const PracticeInfo = styled.div`
  font-size: ${Fonts.EXTRA_SMALL};
  line-height: 14px;
  color: var(--color-text-secondary);
  margin-top: ${Spacings.SPACING_3B};
`;

const practiceStatus = {
  COMPLETED: 1,
  NOT_COMPLETED: 0
};

const PracticeCard = ({ story, onCloseUpdates }) => {
  const { message, created_at: createdAt, object } = story;
  const {
    uid: quizUID,
    author = {},
    goal,
    num_questions: numOfQuestions,
    class_details: classDetails = {},
    status,
    topic_group: topicGroupUID,
    session_uid: sessionUID
  } = object;

  const { name, rank, live_at: liveAt } = classDetails;
  const { first_name: firstName = '', last_name: lastName = '' } = author;
  const { COMPLETED, NOT_COMPLETED } = practiceStatus;
  const { toggleShowSummary } = useFinishSummary();

  const isCompleted = status === COMPLETED;
  const segmentAnalytics = usePracticeSegmentAnalytics(goal.uid);
  const isDarkModeOn = useDarkModeStatus(getIsDarkModeOn);
  const dispatch = useDispatch();
  const { setFinishSummary } = useFinishSummary();
  const { finishPlannerPracticeSession } = usePracticeStore();

  const handleButtonClick = async () => {
    if (isCompleted && sessionUID) {
      finishPlannerPracticeSession({}, false);
      await dispatch(finishPracticeSession(sessionUID));
      onCloseUpdates();
      setFinishSummary({
        goalSlug: goal.slug,
        goalUID: goal.uid,
        sessionId: sessionUID,
        topicType: PRACTICE_TYPE.EACH_TOPIC,
        type: PRACTICE_FLOW_TYPE.AUTO_DPP,
        topicGroup: topicGroupUID,
        topicUID: topicGroupUID,
        quizUID,
        educatorName: `${firstName} ${lastName}`
      });
      return toggleShowSummary();
    }
    if (quizUID && topicGroupUID && goal) {
      return pushRoute({
        pathname: '/goal/[goalSlug]/[goalUID]/practice/[topicUID]',
        query: {
          goalSlug: goal.slug,
          goalUID: goal.uid,
          topic_type: PRACTICE_TYPE.EACH_TOPIC,
          type: PRACTICE_FLOW_TYPE.AUTO_DPP,
          topic_group: topicGroupUID,
          topicUID: topicGroupUID,
          quiz_uid: quizUID,
          educator_name: `${firstName} ${lastName}`,
          source: 'course updates'
        }
      }).then(() =>
        segmentAnalytics(PRACTICE_STARTED, {
          topic_uid: null,
          practice_questions: numOfQuestions,
          topology_id: topicGroupUID,
          topology_name: 'NA',
          topology_level: 'NA',
          last_primary_source_section: 'course updates',
          language: 'NA',
          practice_type: 'Educator Led Practice',
          is_completed: isCompleted ? 'Yes' : 'No'
        })
      );
    }
  };
  return (
    <div>
      <CardHeader
        message={message}
        img="new-practice.svg"
        created_at={createdAt}
      />
      <OuterWrapper padding={Spacings.SPACING_5B}>
        <div>
          <Title>{name}</Title>
          <SubTitle>
            Lesson {rank} • {getDateMonthString(liveAt, false, false)}
          </SubTitle>

          <PracticeInfo>
            {numOfQuestions} questions by {firstName} {lastName}
          </PracticeInfo>
        </div>

        <ActionWrapper>
          <StyledButton
            variant={status === NOT_COMPLETED ? 'filled' : 'hollow'}
            color="black"
            label={
              status === NOT_COMPLETED
                ? 'Start practice'
                : 'View completed practice'
            }
            width={status === NOT_COMPLETED ? '128px' : '220px'}
            maxHeight="sm"
            $isDarkModeOn={isDarkModeOn}
            $textColor={
              status === NOT_COMPLETED
                ? 'var(--color-i-black)'
                : 'var(--color-i-gray-9)'
            }
            onClick={handleButtonClick}
          />
        </ActionWrapper>
      </OuterWrapper>
    </div>
  );
};

export default PracticeCard;
